import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ItineraryItem } from '@/services/itinerary';
import type { Trip } from '@/services/trips';
import {
  fetchTripItineraryItems,
  groupItineraryItemsByDate,
  subscribeToTripItineraryItems,
} from '@/services/itinerary';
import { toLocalDateKey } from '@/utils/dateKeys';
import { toDateKeyInTimezone } from '@/utils/tripItemSchedule';

type CacheEntry = {
  items: ItineraryItem[];
  updatedAt: number;
  listeners: Set<() => void>;
  unsubscribe?: () => void;
};

type UseTripItineraryDayOptions = {
  date?: Date | null;
  /** When set, takes precedence over `date` for bucket selection. */
  dateKey?: string | null;
  /** Trip context for destination trip-day calendar bucketing. */
  trip?: Trip;
  /** Destination IANA timezone; groups items on trip-day calendar labels. */
  timeZone?: string;
  staleMs?: number;
};

type UseTripItineraryDayResult = {
  loading: boolean;
  items: ItineraryItem[];
  allItems: ItineraryItem[];
  dateKey: string | null;
  isStale: boolean;
  lastUpdated: number | null;
  refresh: () => Promise<void>;
};

const FIVE_MINUTES = 5 * 60 * 1000;
const itineraryCache = new Map<string, CacheEntry>();

const resolveDateKey = (options: UseTripItineraryDayOptions): string | null => {
  if (options.dateKey) {
    return options.dateKey;
  }
  if (!options.date) {
    return null;
  }
  if (options.timeZone) {
    return toDateKeyInTimezone(options.date, options.timeZone);
  }
  return toLocalDateKey(options.date);
};

const getOrCreateEntry = (tripId: string): CacheEntry => {
  let entry = itineraryCache.get(tripId);
  if (!entry) {
    entry = {
      items: [],
      updatedAt: 0,
      listeners: new Set(),
    };
    itineraryCache.set(tripId, entry);
  }
  return entry;
};

const hydrateEntry = (tripId: string) => {
  const entry = getOrCreateEntry(tripId);
  if (entry.unsubscribe) {
    return entry;
  }

  entry.unsubscribe = subscribeToTripItineraryItems(tripId, (items) => {
    entry.items = items;
    entry.updatedAt = Date.now();
    entry.listeners.forEach((listener) => listener());
  });

  return entry;
};

const computeDayItems = (
  entry: CacheEntry | undefined,
  dateKey: string | null,
  trip?: Trip,
) => {
  const allItems = entry?.items ?? [];
  if (!dateKey || allItems.length === 0) {
    return {
      dayItems: [],
      allItems,
    };
  }

  const grouped = groupItineraryItemsByDate(
    allItems,
    trip ? { trip } : undefined,
  );
  return {
    dayItems: grouped.get(dateKey) ?? [],
    allItems,
  };
};

export function useTripItineraryDay(
  tripId?: string | null,
  options: UseTripItineraryDayOptions = {},
): UseTripItineraryDayResult {
  const { date = new Date(), staleMs = FIVE_MINUTES, timeZone, dateKey: dateKeyOverride, trip } = options;
  const dateKey = useMemo(
    () => resolveDateKey({ date, dateKey: dateKeyOverride, timeZone }),
    [date, dateKeyOverride, timeZone],
  );

  const [state, setState] = useState(() => {
    if (!tripId) {
      return {
        loading: false,
        items: [],
        allItems: [],
        lastUpdated: null,
        isStale: false,
      };
    }

    const entry = itineraryCache.get(tripId);
    const { dayItems, allItems } = computeDayItems(entry, dateKey, trip);
    const lastUpdated = entry?.updatedAt ?? null;
    const isStale = !lastUpdated || Date.now() - lastUpdated > staleMs;
    return {
      loading: !entry,
      items: dayItems,
      allItems,
      lastUpdated,
      isStale,
    };
  });

  useEffect(() => {
    if (!tripId) {
      return;
    }

    const entry = hydrateEntry(tripId);

    const notify = () => {
      const { dayItems, allItems } = computeDayItems(entry, dateKey, trip);
      setState({
        loading: false,
        items: dayItems,
        allItems,
        lastUpdated: entry.updatedAt || null,
        isStale: !entry.updatedAt || Date.now() - entry.updatedAt > staleMs,
      });
    };

    entry.listeners.add(notify);

    // Emit immediately with the freshest cache snapshot
    notify();

    return () => {
      entry.listeners.delete(notify);

      if (entry.listeners.size === 0) {
        if (entry.unsubscribe) {
          try {
            entry.unsubscribe();
          } catch (error) {
            console.warn('[useTripItineraryDay] Error cleaning up subscription', error);
          }
        }
        itineraryCache.delete(tripId);
      }
    };
  }, [tripId, dateKey, staleMs, timeZone, trip]);

  const refresh = useCallback(async () => {
    if (!tripId) {
      return;
    }

    const entry = getOrCreateEntry(tripId);
    setState((prev) => ({ ...prev, loading: true }));
    try {
      const items = await fetchTripItineraryItems(tripId);
      entry.items = items;
      entry.updatedAt = Date.now();
      entry.listeners.forEach((listener) => listener());
    } catch (error) {
      console.error('[useTripItineraryDay] Failed to refresh itinerary', error);
      setState((prev) => ({ ...prev, loading: false }));
    }
  }, [tripId]);

  return {
    loading: state.loading,
    items: state.items,
    allItems: state.allItems,
    dateKey,
    isStale: state.isStale,
    lastUpdated: state.lastUpdated,
    refresh,
  };
}

export const __testing = {
  resetCache: () => {
    itineraryCache.forEach((entry) => {
      entry.listeners.clear();
      if (entry.unsubscribe) {
        try {
          entry.unsubscribe();
        } catch {
          // Ignore cleanup errors in test helpers
        }
      }
    });
    itineraryCache.clear();
  },
};
